import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Row } from "@/lib/cleaning-engine";
import { getColumns } from "@/lib/cleaning-engine";

export interface DatasetVersion {
  id: string;
  dataset_key: string;
  label: string | null;
  version: number;
  row_count: number;
  columns: string[];
  rows: Row[];
  change_summary: any;
  is_current: boolean;
  created_at: string;
}

/**
 * Versioned snapshots of a working dataset, stored in `dataset_versions`.
 * "Apply changes" writes a new version; "Revert" restores the previous one.
 * Nothing is lost — reverting moves the pointer, it does not delete history.
 */
export const useDatasetVersions = (datasetKey?: string) => {
  const [versions, setVersions] = useState<DatasetVersion[]>([]);
  const [isBusy, setIsBusy] = useState(false);

  const load = useCallback(async (key = datasetKey) => {
    if (!key) return [];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from("dataset_versions")
      .select("*")
      .eq("user_id", user.id)
      .eq("dataset_key", key)
      .order("version", { ascending: false });
    if (error) throw error;
    const list = (data ?? []) as unknown as DatasetVersion[];
    setVersions(list);
    return list;
  }, [datasetKey]);

  const saveVersion = useCallback(
    async (params: {
      datasetKey: string;
      label?: string;
      rows: Row[];
      changeSummary?: unknown;
    }) => {
      setIsBusy(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Please sign in to save your dataset.");

        const { data: latest } = await supabase
          .from("dataset_versions")
          .select("version")
          .eq("user_id", user.id)
          .eq("dataset_key", params.datasetKey)
          .order("version", { ascending: false })
          .limit(1)
          .maybeSingle();

        const nextVersion = (latest?.version ?? 0) + 1;

        await supabase
          .from("dataset_versions")
          .update({ is_current: false })
          .eq("user_id", user.id)
          .eq("dataset_key", params.datasetKey);

        const { data, error } = await supabase
          .from("dataset_versions")
          .insert({
            user_id: user.id,
            dataset_key: params.datasetKey,
            label: params.label ?? null,
            version: nextVersion,
            row_count: params.rows.length,
            columns: getColumns(params.rows) as any,
            rows: params.rows as any,
            change_summary: (params.changeSummary ?? {}) as any,
            is_current: true,
          })
          .select()
          .single();
        if (error) throw error;
        await load(params.datasetKey);
        return data as unknown as DatasetVersion;
      } finally {
        setIsBusy(false);
      }
    },
    [load],
  );

  /** Restore the version immediately before the current one. */
  const revertToPrevious = useCallback(
    async (key: string) => {
      setIsBusy(true);
      try {
        const list = await load(key);
        if (list.length < 2) {
          throw new Error("There is no earlier version to go back to.");
        }
        const [current, previous] = list;
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Please sign in.");

        await supabase
          .from("dataset_versions")
          .update({ is_current: false })
          .eq("id", current.id);
        await supabase
          .from("dataset_versions")
          .update({ is_current: true })
          .eq("id", previous.id);

        await load(key);
        return previous;
      } finally {
        setIsBusy(false);
      }
    },
    [load],
  );

  return { versions, load, saveVersion, revertToPrevious, isBusy };
};
